import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description as MatchReportIcon,
  CheckCircle as SubmittedIcon,
  Warning as PendingIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import unifiedApi from '../utils/unifiedApi';

const PendingMatchReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let cancelled = false;
    const fetchReports = async () => {
      try {
        setLoading(true);
        const data = await unifiedApi.getPendingMatchReports();
        if (!cancelled) {
          setReports(data || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load pending match reports.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReports();
    return () => { cancelled = true; };
  }, []);

  const formatGameDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Group reports by league
  const groupedByLeague = reports.reduce((acc, report) => {
    const key = report.leagueId;
    if (!acc[key]) {
      acc[key] = { leagueName: report.leagueName, reports: [] };
    }
    acc[key].reports.push(report);
    return acc;
  }, {});

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <MatchReportIcon color="primary" />
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
          Pending Match Reports
        </Typography>
        {reports.length > 0 && (
          <Chip
            data-testid="pending-count-badge"
            label={reports.length}
            color="error"
            size="small"
            clickable={false}
            onClick={() => {}}
            sx={{ fontWeight: 'bold' }}
          />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {reports.length === 0 && !error && (
        <Alert severity="info">
          No pending match reports. All caught up!
        </Alert>
      )}

      {Object.entries(groupedByLeague).map(([leagueId, { leagueName, reports: leagueReports }]) => (
        <Box key={leagueId} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600} color="text.secondary">
              {leagueName}
            </Typography>
            <Chip
              label={leagueReports.length}
              size="small"
              variant="outlined"
              clickable={false}
              onClick={() => {}}
            />
          </Stack>

          <Stack spacing={1.5}>
            {leagueReports.map((report) => (
              <Card key={`${report.gameId}-${report.userTeamId}`} variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    justifyContent="space-between"
                    spacing={isMobile ? 1 : 2}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatGameDate(report.gameDate)}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {report.homeTeamName} vs {report.awayTeamName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submitting for: {report.userTeamName}
                      </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      {report.opponentHasSubmitted ? (
                        <Chip
                          icon={<SubmittedIcon />}
                          label="Opponent submitted"
                          size="small"
                          color="success"
                          variant="outlined"
                          clickable={false}
                          onClick={() => {}}
                        />
                      ) : (
                        <Chip
                          icon={<PendingIcon />}
                          label="Neither submitted"
                          size="small"
                          color="warning"
                          variant="outlined"
                          clickable={false}
                          onClick={() => {}}
                        />
                      )}
                      <Button
                        component={Link}
                        to={`/games/${report.gameId}/teams/${report.userTeamId}/report`}
                        variant="contained"
                        size="small"
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Submit Report
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ))}
    </Container>
  );
};

export default PendingMatchReports;
